/**
 * Array narrowing predicates and coercers. Pure-leaf module: NO value imports.
 *
 * `asArrayOrEmpty` and `isStringArray` are NOT interchangeable: one CHECKS element type and narrows, the other only
 * checks array-ness and asserts the element type at the boundary. Pick by whether the elements are validated.
 *
 * THREE COERCERS, ONE ARRAY-NESS TEST, THREE DIFFERENT ANSWERS FOR A NON-ARRAY -- read this before picking one.
 * `asArrayOrEmpty` DISCARDS it (`[]`), `wrapArray` WRAPS it (`[value]`, falsy included), and `ensureArray` wraps it
 * unless it is falsy, in which case it discards. Confusing the first two turns 'no rows' into 'one row' at an
 * envelope-reading call site; confusing the last two silently drops a legitimate `0`, `''` or `false`. The wrap half
 * is spelled ONCE, in `wrapArray`, and `ensureArray` calls it -- the same same-file delegation `hasNonEmptyArrayAt`
 * takes onto `isNonEmptyArray`, so the two cannot diverge on array-ness while differing on the falsy question.
 *
 * `wrapArray` and `ensureArray` live HERE for the reason the paragraph below records for their neighbours and for one
 * more: a `utils/` sibling may not value-import another, so split across two `utils/` leaves the shared array-ness
 * ternary COULD NOT be spelled once. The const/ home is what makes the delegation legal.
 *
 * `isNonEmptyArray` / `hasNonEmptyArrayAt` belong HERE rather than under `utils/`, and that placement is load-bearing:
 * `QUAL-RAW-ARRAY-CHECK-001` prescribes importing them as its cure, while `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bans a
 * `@federa/utils` value import inside `utils/` -- so a `utils/` home makes that cure unreachable and forces the rule to
 * carry a `utils/**` exclusion. Their bodies are import-free, so `const/` (the one layer every `utils/` leaf MAY
 * value-import) is what keeps the cure reachable and the rule enforceable across `utils/`. Do not relocate them.
 */

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. UNCHECKED elements -- an array passes
 * through asserted as `T[]`, anything else (the flow-template-resolved `null`/`undefined` a node that never ran
 * or failed leaves behind) becomes `[]`. The canonical reader for a rows-shaped step output, where the caller
 * already knows the row shape from the query. Use {@link isStringArray} when the elements must be VERIFIED.
 *
 * THE COUNT SPELLING IS THIS MEMBER TOO, and it was the largest un-drained respell of it. Censused 2026-09-05 over the
 * whole tracked `.ts`/`.tsx` corpus, `Array.isArray(x) ? x.length : 0` was spelled at TWELVE sites in seven layers --
 * apps/ (3), const/, flows/, spa/ (2), tools/ (3), utils/ (2). It is this coercer plus `.length` and nothing else:
 * `asArrayOrEmpty(x).length` is value-identical for every input, since the miss branch yields `[]` whose length IS 0.
 * The three apps/ sites adopted first (the properties quick-stats offer count and the offers tab counts on the
 * properties and transactions detail pages). EIGHT OF THE REMAINING NINE ADOPTED on 2026-09-05 --
 * `const/search-journey-exercise.ts`, the issue-status reaper flow, `spa/market-maker/frontend/mm/page.helpers.ts`
 * (two, adjacent), `tools/embeddings/vector-query.ts`, `tools/property/property-data-aggregate-summary.ts`,
 * `utils/assistant/conversation-ai-control-guard.ts` and `utils/property/core-payload-projection.ts`. The ninth, the
 * Root-cause map completeness census's host step, was held off because a concurrent lane had that leaf modified at
 * first touch, and went whole with the map at the operator's word (2026-09-08).
 *
 * THE PAIRED FORBID-RULE IS `QUAL-ARRAY-COUNT-TERNARY-SSOT-BYPASS-001`, the fourth member of the false-arm family over
 * this one array-ness test and the only one whose tail answers a NUMBER. It was owed because no incumbent could reach
 * the shape: the three sibling coercion rules each require the true arm to BE the backreferenced operand, and
 * `x.length` is not; `QUAL-GUARD-COERCER-HALF-ADOPTION-001`'s IDENTITY TEST requires the consequent to denote the same
 * property path as the guard's argument, and `x.length` is a DIFFERENT consequent -- the exact shape that rule's third
 * false-positive class holds out by construction, so widening it there would re-admit that class. This leaf is that
 * rule's one `excludeGlobs` entry, on the ground that its body IS the cure and cannot adopt itself.
 */
export function asArrayOrEmpty<T>(value: unknown): T[] {
	return Array.isArray(value) ? (value as T[]) : [];
}

/**
 * The NULL-returning twin of {@link asArrayOrEmpty}, and the array sibling of `asPlainObjectOrNull`
 * (`const/record-guards.ts`) -- the record family already had both halves and this one did not, which is why the
 * ternary kept getting hand-rolled: re-censused 2026-09-05 over the whole tracked `.ts`/`.tsx`/`.mjs`/`.cjs` corpus
 * (33,036 files, `.claude/worktrees/**` pruned), `Array.isArray(x) ? x : null` was spelled at FOURTEEN sites in SIX
 * layers -- const/, flows/, mfe/, tools/, utils/ and a scanner rule leaf -- one of them under a type-specialized
 * rowsOrNull wrapper (the since-deleted issue id-resolution leaf) carrying no more than this body, which DELEGATED here.
 * ALL FOURTEEN ADOPTED in the same change, so the class is at zero.
 *
 * PAIRED FORBID-RULE: `QUAL-ARRAY-NULL-TERNARY-SSOT-BYPASS-001`, the third member of the false-arm family over this
 * one array-ness test -- `QUAL-ARRAY-WRAP-TERNARY-SSOT-BYPASS-001` owns the `: [x]` tail and {@link wrapArray} cures
 * it, `QUAL-STRING-LIST-COERCION-SSOT-BYPASS-001` owns the `: []` tail and {@link asArrayOrEmpty} cures it, and this
 * member cures the `: null` tail. No expression spells two of the three tails, which is what keeps the three rules
 * from ever claiming the same carrier. This leaf is that rule's ONE `excludeGlobs` entry, on the ground that its body
 * IS the cure and cannot adopt itself.
 *
 * NOT interchangeable with {@link asArrayOrEmpty}, which is the reason both exist: `[]` says "no rows", `null` says
 * "this read did not produce an array at all". A server-preload field that must stay distinguishable from an empty
 * result -- so the client falls back to its own fetch rather than rendering an empty list -- needs THIS one, and
 * collapsing the two would erase the difference silently.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ beside its siblings so pure utils/
 * leaves can value-import it without a forbidden cross-utils sibling dependency. UNCHECKED elements, exactly as
 * {@link asArrayOrEmpty} is -- use {@link isStringArray} when the elements must be VERIFIED.
 */
export function asArrayOrNull<T>(value: unknown): T[] | null {
	return Array.isArray(value) ? (value as T[]) : null;
}

/**
 * The WRAPPING coercer: an array passes through by REFERENCE, anything else becomes a one-element array -- `0`, `''`
 * and `false` included. This is the family's only spelling of the array-ness ternary; {@link ensureArray} composes it
 * and nothing else should re-spell it. Use it for a value that is legitimately `T | T[]` and where every member
 * matters (a single ref vs a ref list, a single update vs a batch, one id vs many). Use {@link asArrayOrEmpty}
 * instead when a non-array means 'no rows' rather than 'one row'.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed in const/ so pure utils/ leaves can value-import it without
 * a forbidden cross-utils sibling dependency, and so {@link ensureArray} can delegate to it -- two utils/ leaves
 * cannot.
 */
export function wrapArray<T>(value: T | T[]): T[] {
	return Array.isArray(value) ? value : [value];
}

/**
 * {@link wrapArray} with a FALSY DISCARD in front: `null`, `undefined`, `0`, `''` and `false` all answer `[]`, and
 * every other non-array is wrapped. The nullish half is what its call sites want -- an optional `string | string[]`
 * glob field, an optional message payload -- and the rest of the falsy set rides along with it, which is a real
 * behavioural difference from {@link wrapArray} and not a detail: a numeric `0` or an empty-string member reaching
 * this coercer is DELETED, not wrapped. Where a falsy scalar must survive, call {@link wrapArray} directly.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): same const/ home and same reason as its neighbours. The
 * array-ness decision is NOT re-spelled here -- it delegates, so the two coercers cannot drift apart on the one
 * question they agree about.
 */
export function ensureArray<T>(value: T | T[] | null | undefined): T[] {
	if (!value) return [];
	return wrapArray(value);
}

/**
 * The array contributed by whichever candidate ACTUALLY RAN: the first argument that is an array wins, and `[]` when
 * none is. The BRANCH-COALESCE sibling of {@link asArrayOrEmpty}, not a variant of it -- a step whose arm was skipped
 * resolves to `null`, so array-ness (never emptiness) is the discriminator, and an arm that ran and legitimately
 * matched nothing must still win over a later arm that never ran at all.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): same const/ home and same reason as its neighbours -- the
 * consumers are pure `utils/` leaves that may not value-import a `utils/` sibling. Spelled as a nested
 * `Array.isArray(a) ? a : Array.isArray(b) ? b : []` ternary at three sites (`utils/groups/group-read-shapes.ts`
 * search-vs-page, `utils/ledger/ledger-flow-envelope.ts` data.rows-vs-body.results, `flows/action-instance-list.ts`
 * owner-vs-group) before the fold. UNCHECKED elements, exactly like `asArrayOrEmpty`: supply the type argument.
 */
export function firstArrayOrEmpty<T>(...candidates: readonly unknown[]): T[] {
	for (const candidate of candidates) {
		if (Array.isArray(candidate)) return candidate as T[];
	}
	return [];
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic guard homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. CHECKED elements -- every entry must be a
 * string, so an empty array passes and a mixed array does not. Unlike {@link asArrayOrEmpty} this is a true type
 * guard with a per-element cost, for values crossing a trust boundary (parsed JSON, DO storage reads).
 */
export function isStringArray(value: unknown): value is string[] {
	return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

/** `value` when it {@link isStringArray}, else `undefined` -- the optional string-list read of an untyped options bag
 *  (a rule's `opts.trackedFiles`) the scanners each spelled as an `Array.isArray` test plus an `as readonly string[]`
 *  cast. Checked, so the cast has nothing to hide.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link isStringArray}. */
export function stringArrayOrUndefined(value: unknown): readonly string[] | undefined {
	return isStringArray(value) ? value : undefined;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the canonical non-empty-array guard, homed in const/ so pure
 * utils/ leaves can value-import it. The generic overload preserves the element type when the input is a typed array.
 * This is the body `QUAL-RAW-ARRAY-CHECK-001` names as the cure for every inline `Array.isArray(x) && x.length > 0`.
 */
export function isNonEmptyArray<T>(value: T[] | null | undefined): value is [T, ...T[]];
export function isNonEmptyArray(value: unknown): value is unknown[];
export function isNonEmptyArray(value: unknown): value is unknown[] {
	return Array.isArray(value) && value.length > 0;
}

/** An array of EXACTLY one entry -- the "one appended entry per write" shape a record door refuses otherwise.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the one-element sibling of {@link isNonEmptyArray}; the framing
 *  projection gate and the terminal-withdrawal claims each spelled the `Array.isArray` test plus a length compare. */
export function isSingleElementArray(value: unknown): value is [unknown] {
	return Array.isArray(value) && value.length === 1;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a value that may arrive as an array, collapsed to its FIRST element
 * when it is a non-empty array -- and passed through unchanged otherwise, an EMPTY array included (the offer
 * normalizer returns it as-is and a caller then reads it back as the field). The return type says so: an array input
 * answers its element OR itself. The offer funding-source and property raw-id normalizers each spelled this ternary.
 */
export function firstElementOrSelf<V>(value: V): V extends readonly (infer E)[] ? E | V : V;
export function firstElementOrSelf(value: unknown): unknown {
	return isNonEmptyArray(value) ? value[0] : value;
}

/**
 * The members as the NON-EMPTY tuple a record arm carries (`[first, ...rest]`), or undefined when there are none and
 * the arm is not taken.
 * @runtime-derived-ssot-helper (const-leaf-pinned): the one reshaping of a filtered list into an optional non-empty
 * member, which the evidence readers and the standard-ref reader each spelled through their own destructure.
 */
export function nonEmptyTupleOrUndefined<T>(items: readonly T[]): [T, ...T[]] | undefined {
	const [first, ...rest] = items;
	return first === undefined ? undefined : [first, ...rest];
}

/** The two members of a list that holds EXACTLY two, or null for any other length -- the `const [a, b, ...rest] =
 *  x.split(sep)` guard a dotted-token resolver and a two-word marker parser each spelled against `undefined` and a
 *  leftover rest. @runtime-derived-ssot-helper (const-leaf-pinned): the two-member sibling of the tuple shapers above. */
export function exactPairOrNull<T>(items: readonly T[]): readonly [T, T] | null {
	const [first, second, ...rest] = items;
	return first === undefined || second === undefined || rest.length > 0 ? null : [first, second];
}

/** The one member of a list that holds EXACTLY one, or undefined for any other length -- the "is this batch already a
 *  single file" read a fatal-containment replay asks before it splits the batch. Answers the member rather than a
 *  boolean, so the caller keeps it narrowed.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the one-member sibling of the tuple shapers above; the eslint and
 *  prettier safe-fix replays each spelled `const [only] = batch` plus a length compare. */
export function soleMemberOrUndefined<T>(items: readonly T[]): T | undefined {
	const [only] = items;
	return items.length === 1 ? only : undefined;
}

/** The index bucket itself when it holds TWO members or more, else undefined -- the "is anyone else in here" read a
 *  collision index answers before it looks for the other site. Answers the bucket rather than a boolean, so the
 *  `undefined` arm is honest and the caller keeps the narrowed list.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the `!sites || sites.length < 2` guard two export-name rules spelled. */
export function twoOrMoreOrUndefined<A extends readonly unknown[]>(items: A | undefined): A | undefined {
	return items !== undefined && items.length >= 2 ? items : undefined;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a NON-EMPTY list of NON-EMPTY strings -- the shape a caller's query
 * list or a resolver's id list must have before anything reads it. Stricter than {@link isStringArray} on both axes (an
 * empty list and an empty member each refuse), and a true guard, so the caller needs no cast afterwards.
 */
export function isFilledStringList(value: unknown): value is string[] {
	return isNonEmptyArray(value) && value.every((entry) => typeof entry === 'string' && entry.length > 0);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the `.filter` predicate that drops EMPTY members -- a split's empty
 * segments, a list's empty strings, an empty bucket -- spelled `(x) => x.length > 0` at forty-plus sites across seven
 * layers before this fold. For a value ALREADY KNOWN to carry a `length`: it asks nothing about array-ness (that is
 * {@link isNonEmptyArray}'s question) and it does NOT trim, so a whitespace-only string passes, unlike
 * `isNonEmptyString` (`@federa/const/string-guards`), which trims first.
 */
export function hasLength(value: { readonly length: number }): boolean {
	return value.length > 0;
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): map-then-keep-non-null in one pass, homed in const/ so pure
 *  utils/ leaves can value-import it (a utils sibling home would be a forbidden cross-utils import for exactly the
 *  callers that need it). The collect loop `for (x of items) { const y = map(x); if (y !== null) out.push(y) }` was
 *  spelled verbatim across layers (witnessed in `utils/` and in `parseClaudemdCriticalRulesHeaders`, tools
 *  rule-helpers); `undefined` is deliberately NOT dropped -- the mappers in
 *  this idiom signal a skipped item with `null` only, and silently absorbing `undefined` would mask a mapper defect. */
export function mapNotNull<A, B>(items: readonly A[], map: (item: A) => B | null): B[] {
	const out: B[] = [];
	for (const item of items) {
		const mapped = map(item);
		if (mapped !== null) out.push(mapped);
	}
	return out;
}

/** True when `obj` carries a non-empty array at `property`. Lives beside {@link isNonEmptyArray}, which defines it.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): delegates the emptiness decision to the same-file
 *  {@link isNonEmptyArray}, so the two cannot diverge, and shares its const/ home for the reason the module docstring
 *  records -- `QUAL-RAW-ARRAY-CHECK-001` prescribes importing it as the cure, which a `utils/` home would make
 *  unreachable from `utils/` itself. */
export function hasNonEmptyArrayAt(obj: unknown, property: string): boolean {
	return obj != null && isNonEmptyArray((obj as Record<string, unknown>)[property]);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): true when the two arrays share AT LEAST ONE member -- the
 * `left.some((x) => right.includes(x))` idiom, spelled once.
 *
 * DISTINCT FROM `includesAny` (`@federa/const/string-guards`) AND THAT IS THE WHOLE POINT: the two are the SAME TEXT
 * SHAPE over different contracts. There the receiver of `.includes` is a STRING and the question is SUBSTRING
 * containment; here it is an ARRAY and the question is element MEMBERSHIP. A single helper covering both would be
 * byte-invisible at the call site and behaviour-changing wherever the receiver's kind changed -- `'abc'.includes('b')`
 * is true and `['abc'].includes('b')` is false. The card that commissioned this pair required every site's direction
 * be enumerated from the haystack's own declaration BEFORE adopting, never inferred from the spelling; three of the
 * twenty-six carriers were this side.
 *
 * SYMMETRIC IN RESULT: `arraysIntersect(a, b)` and `arraysIntersect(b, a)` agree. Argument order is preserved from the
 * call site for readability only, and comparison is `===` (SameValueZero) throughout, inherited from
 * `Array.prototype.includes`.
 *
 * NOT a set-subtraction and not a dedupe -- `tokensNotIn` (`@federa/const/tokens-not-in`) answers the difference
 * question, and answers it one-directionally on purpose.
 */
export function arraysIntersect<T>(left: readonly T[], right: readonly T[]): boolean {
	return left.some((member) => right.includes(member));
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): an `indexOf` / `findIndex` answer as the position it found, or
 *  null for the `-1` that means none -- the sentinel read once, so a found position of 0 can never read as absent. */
export function foundIndexOrNull(index: number): number | null {
	return index === -1 ? null : index;
}

/**
 * The member at `index`, or a THROW naming the index and the length.
 *
 * WHY THIS EXISTS RATHER THAN A NON-NULL ASSERTION. Under `noUncheckedIndexedAccess` every indexed read is
 * `T | undefined`, and the two ways to satisfy the checker are not equivalent: `!` erases the question at COMPILE time
 * and leaves a runtime `undefined` free to travel to whatever reads it next, surfacing far from the read that produced
 * it. This helper answers it at RUNTIME and names the read, so an out-of-range index fails where it happened, with the
 * index and the length in the message, rather than as a property access on undefined three frames later.
 *
 * IT TAKES `ArrayLike<T | undefined>` ON PURPOSE, and that is what lets ONE helper serve both shapes its callers have:
 * a plain array whose members are `T`, and a `RegExpExecArray`, whose capture groups are genuinely optional -- an
 * unmatched optional group reads `undefined` at a defined index. Narrowing the parameter to `readonly T[]` would leave
 * capture reads to a second spelling, which is the drift this leaf exists to prevent.
 *
 * `what` is the CALLER's noun for the collection rather than a message template: it is prefixed to the throw so a
 * reader learns which read failed without needing a stack.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): pinned with its leaf -- nine of this leaf's exported guards are
 * value-bound by `utils/` leaves that may not value-import a utils sibling (QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001, no
 * allowlist), so the array-guard floor lives here whole and a utils/ home for one member would split it.
 */
export function requireArrayMemberAt<T>(items: ArrayLike<T | undefined>, index: number, what: string): T {
	const member = items[index];
	if (member === undefined) {
		throw new Error(`${what}: no member at index ${String(index)} (length ${String(items.length)})`);
	}
	return member;
}

/** Append `item` unless the list already holds a member of the same `name` -- FIRST name wins, in list order. The
 *  name-keyed dedup push a column fold and a glob-ref index each spelled as `if (!list.some(...)) list.push(item)`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): pinned with its leaf, beside the array floor. */
export function pushUniqueByName<T extends { readonly name: string }>(list: T[], item: T): void {
	if (!list.some((existing) => existing.name === item.name)) list.push(item);
}
